import styles from './Dashboard.module.css';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { useState, useEffect, useMemo, useCallback } from 'react';

import * as api from '../../api.js';

import { useUser } from '../../hooks/useUser';

import { MyInfo } from '../MyInfo/MyInfo';
import { MessageBox } from '../MessageBox/MessageBox';
import { Items } from '../Items/Items';

const isNewItem = (id) => /^new-\d+(-[a-zA-Z0-9]+)?$/.test(id);

export const Dashboard = () => {
    const [ error, setError ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ items, setItems ] = useState([]);
    
    const { user, token, error: userError } = useUser();

    // Computed values
    const sellerItems = useMemo(() => items, [ items ]);

    // MARK: Handlers
    const handleNewItem = () => {
        const newItem = {
            id: `new-${ Date.now() }`,
            name: '',
            brand: '',
            desc: '',
            size: '',
            color: '',
            gender: '',
            category: '',
            image: '',
            price: 0,
            quantity: 0,
            condition: ''
        };

        setItems(prev => [ ...prev, newItem ]);
    };

    const handleSaveItem = useCallback(async (item, photo) => {
        if (!item) return;

        try {
            setLoading(true);

            const ret = isNewItem(item.id) ? await api.addItem({ ...item, seller: user.id }, token) : await api.updateItem({ ...item, seller: user.id }, token);

            if (!ret?.error && photo) {
                const result = await api.uploadPhoto(ret, photo, token);

                if (result.error) {
                    setError(result.error);

                    return;
                }
            }

            if (ret?.error) {
                setError(ret.error);

                return;
            }

            if (isNewItem(item.id)) {
                const savedItem = { ...item, id: ret, seller: user.id };

                setItems(prev => prev.map(i => i.id === item.id ? savedItem : i));
            } else {
                setItems(prev => prev.map(i => i.id === item.id ? item : i));
            }
        } catch (error) {
            setError('Failed to update item');
        } finally {
            setLoading(false);
        }
    }, [ user, token ]);

    const handleCopyItems = async (items) => {
        if (!items || items.length === 0) return;

        try {
            setLoading(true);

            const newItems = items.map(item => ({
                id: `new-${ Date.now() }-${ Math.random().toString(36).substring(2, 9) }`,
                name: item.name || '',
                brand: item.brand || '',
                desc: item.desc || '',
                size: item.size || '',
                color: item.color || '',
                gender: item.gender || '',
                category: item.category || '',
                image: item.image || '',
                imageUrl: item.imageUrl || '',
                price: item.price || 0,
                quantity: item.quantity || 0,
                condition: item.condition || '',
                feePaid: false // New copied items should not be marked as paid
            }));

            // Add the new items to state immediately so they appear in the UI
            setItems(prev => [ ...prev, ...newItems ]);

            // Then save them to the backend
            await Promise.all(newItems.map((item) => handleSaveItem(item)));
        } catch (error) {
            setError('Failed to copy items');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveItems = async (ids) => {
        const ret = ids && await api.deleteItems(ids, token);

        if (ret?.error) {
            setError(ret.error);
        } else {
            setItems(prev => prev.filter(i => !ids.includes(i.id)));

            setError(null);
        }
    };

    const handleCancel = () => {
        setItems(prev => prev.filter(i => !isNewItem(i.id)));
    };

    const handlePaymentSuccess = (purchasedItems) => {
        // Mark purchased items as listing fee paid
        setItems(prev => prev.map(item =>
            purchasedItems.some(purchased => purchased.id === item.id)
                ? { ...item, feePaid: true }
                : item
        ));
    };

    useEffect(() => {
        if (!user) return;  // Wait for user to be loaded

        const fetchItems = async () => {
            try {
                setLoading(true);

                const itemsData = await api.getItemsBySeller(user.id, token);

                if (itemsData.error) {
                    setError(itemsData.error);

                    setLoading(false);

                    return;
                }

                setItems(itemsData);
            } catch (error) {
                console.error(error);
                
                setError('Failed to fetch items');
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, [ user, token ]); // Depend on user from context

    // MARK: Render
    if (userError) {
        return <Alert variant="filled" severity="error">{ userError }</Alert>;
    }

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert variant="filled" severity="error" action={
            <Button color="inherit" size="small" onClick={ () => setError(null) } >Dismiss</Button>
        }>{ error }</Alert>;
    }

    return (
        <div className={ styles.dashboard }>
            <MyInfo user={ user } />

            <MessageBox />

            <Items
                myList={ true }
                items={ sellerItems }
                isPatroller={ user.isPatroller }
                onNewItem={ handleNewItem }
                onSaveItem={ handleSaveItem }
                onCopyItems={ handleCopyItems }
                onRemoveItems={ handleRemoveItems }
                onCancel={ handleCancel }
                onPaymentSuccess={ handlePaymentSuccess }
            />
        </div>
    );
};